import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, MapPin, Sparkles } from 'lucide-react';
import { api } from '../lib/api';
import { flagFor, specialIconFor, isCocaCola } from '../lib/flags';
import { useI18n } from '../lib/i18n';

type Section = 'duplicates' | 'missing';

export function PublicCollection() {
  const { slug } = useParams<{ slug: string }>();
  const { t, lang } = useI18n();
  const [tab, setTab] = useState<Section>('duplicates');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const { data, isLoading, error } = useQuery({
    queryKey: ['public-collection', slug],
    queryFn: () => api.publicCollection(slug!),
    enabled: !!slug,
    retry: false,
  });

  const groups = useMemo(() => {
    if (!data) return [];
    const items = tab === 'duplicates' ? data.duplicates : data.missing;
    const byPage = new Map<number, any[]>();
    for (const s of items) {
      if (!byPage.has(s.pageNumber)) byPage.set(s.pageNumber, []);
      byPage.get(s.pageNumber)!.push(s);
    }
    return [...byPage.entries()]
      .map(([pageNumber, arr]) => {
        const title = arr[0]?.pageTitle ?? `Página ${pageNumber}`;
        const totalExtras = arr.reduce((sum, s) => sum + (s.extras ?? 1), 0);
        return { pageNumber, title, items: arr, totalExtras };
      })
      .sort((a, b) => a.pageNumber - b.pageNumber);
  }, [data, tab]);

  function toggle(pn: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(pn) ? next.delete(pn) : next.add(pn);
      return next;
    });
  }

  if (isLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-void text-foam">
        <div className="animate-pulse text-haze">Loading…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen grid place-items-center bg-void text-foam p-6 text-center">
        <div className="max-w-sm">
          <h1 className="font-display font-extrabold text-2xl mb-2">
            {lang === 'es' ? 'Colección no encontrada' : 'Collection not found'}
          </h1>
          <p className="text-haze text-sm mb-6">
            {lang === 'es'
              ? 'Este link no está disponible o es privado.'
              : 'This link is unavailable or private.'}
          </p>
          <Link to="/" className="text-cyan underline">← Pannini 2026</Link>
        </div>
      </div>
    );
  }

  const { user, stats } = data;

  return (
    <div className="min-h-screen bg-void text-foam pb-20">
      {/* Header con datos del owner */}
      <header className="relative overflow-hidden border-b border-line/40">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan/10 via-transparent to-lime/5" />
        <div className="relative max-w-2xl mx-auto px-4 pt-8 pb-6">
          <div className="flex items-center gap-4">
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="h-16 w-16 rounded-full object-cover border-2 border-cyan/40" />
            ) : (
              <div className="h-16 w-16 rounded-full grid place-items-center bg-cyan/15 border-2 border-cyan/40 font-display font-extrabold text-2xl text-cyan">
                {user.displayName[0]?.toUpperCase() ?? '?'}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-2xs uppercase tracking-widest text-haze">
                {lang === 'es' ? 'Álbum de' : "Album by"}
              </p>
              <h1 className="font-display font-extrabold text-2xl truncate">
                {user.displayName}
              </h1>
              {user.city && (
                <div className="mt-0.5 text-xs text-haze flex items-center gap-1">
                  <MapPin size={12} />
                  {user.city}
                </div>
              )}
            </div>
          </div>

          <div className="mt-5 grid grid-cols-4 gap-2 text-center">
            <Stat n={stats.completionPct} label={lang === 'es' ? '% listo' : 'done'} accent="lime" suffix="%" />
            <Stat n={stats.have} label={lang === 'es' ? 'tengo' : 'have'} accent="cyan" />
            <Stat n={stats.missing} label={lang === 'es' ? 'faltan' : 'missing'} accent="flame" />
            <Stat n={stats.duplicates} label={lang === 'es' ? 'repes' : 'dupes'} accent="cyan" />
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="sticky top-0 z-10 bg-void/95 backdrop-blur border-b border-line/40">
        <div className="max-w-2xl mx-auto px-4 py-3 flex gap-2">
          <TabButton active={tab === 'duplicates'} onClick={() => { setTab('duplicates'); setExpanded(new Set()); }}>
            {lang === 'es' ? `Repes · ${stats.duplicates}` : `Dupes · ${stats.duplicates}`}
          </TabButton>
          <TabButton active={tab === 'missing'} onClick={() => { setTab('missing'); setExpanded(new Set()); }}>
            {lang === 'es' ? `Faltan · ${stats.missing}` : `Missing · ${stats.missing}`}
          </TabButton>
        </div>
      </div>

      {/* Lista agrupada por país */}
      <div className="max-w-2xl mx-auto px-4 py-5 space-y-2">
        {groups.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center text-haze text-sm">
            {tab === 'duplicates'
              ? (lang === 'es' ? 'Sin repes por acá.' : 'No dupes yet.')
              : (lang === 'es' ? 'Álbum completo 🎉' : 'Album complete 🎉')}
          </div>
        ) : (
          groups.map((g) => {
            const isOpen = expanded.has(g.pageNumber);
            const flag = flagFor(g.title);
            return (
              <div
                key={g.pageNumber}
                className="glass-strong rounded-2xl border border-line/60 overflow-hidden"
              >
                <button
                  onClick={() => toggle(g.pageNumber)}
                  className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.02] transition"
                >
                  {isCocaCola(g.title) ? (
                    <div className="shrink-0 grid h-11 w-11 rounded-xl place-items-center bg-flame border-2 border-flame font-display font-extrabold text-xl text-white italic">
                      C
                    </div>
                  ) : (
                    <div className="shrink-0 grid h-11 w-11 rounded-xl place-items-center text-2xl bg-cyan/15 border border-cyan/30">
                      {flag ?? specialIconFor(g.title) ?? '✦'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-display font-extrabold text-base truncate">{g.title}</h3>
                      <span className="text-2xs font-bold tabular-nums shrink-0 text-cyan">
                        {tab === 'duplicates' ? `×${g.totalExtras}` : `${g.items.length}`}
                      </span>
                    </div>
                    <div className="text-2xs text-haze mt-0.5">
                      {g.items.length} {lang === 'es' ? 'stickers' : 'stickers'}
                    </div>
                  </div>
                  <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.25 }} className="shrink-0 text-haze">
                    <ChevronDown size={20} />
                  </motion.div>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="px-3 pb-4 pt-1 border-t border-line/40">
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                          {g.items.map((s: any) => (
                            <div
                              key={s.id}
                              className="rounded-xl border border-line/60 bg-graphite/40 p-2 text-center"
                            >
                              <div className="font-mono text-xs font-bold text-foam">
                                {s.code?.replace(/-/g, ' ') ?? s.number}
                              </div>
                              {s.playerName && (
                                <div className="mt-0.5 text-2xs text-haze line-clamp-1">{s.playerName}</div>
                              )}
                              {s.rarity === 'shiny' && (
                                <div className="mt-1 text-2xs text-cyan flex items-center justify-center gap-0.5">
                                  <Sparkles size={10} /> shiny
                                </div>
                              )}
                              {tab === 'duplicates' && (s.extras ?? 0) > 1 && (
                                <div className="mt-1 text-2xs text-lime font-bold">×{s.extras}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })
        )}

        {/* CTA final */}
        <div className="mt-8 rounded-2xl glass-strong border border-cyan/30 p-5 text-center">
          <p className="text-sm text-haze mb-3">
            {lang === 'es'
              ? '¿Querés intercambiar con ' + user.displayName.split(' ')[0] + '?'
              : `Want to trade with ${user.displayName.split(' ')[0]}?`}
          </p>
          <Link
            to="/register"
            className="inline-block rounded-full bg-lime text-ink font-semibold px-6 py-2.5 shadow-glow-lime hover:scale-105 transition"
          >
            {lang === 'es' ? 'Crear cuenta gratis' : 'Create free account'}
          </Link>
          <p className="mt-3 text-2xs text-haze">
            {lang === 'es'
              ? '¿Ya tenés cuenta? '
              : 'Already have an account? '}
            <Link to="/login" className="text-cyan underline">
              {lang === 'es' ? 'Ingresar' : 'Sign in'}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Stat({ n, label, accent, suffix }: { n: number; label: string; accent: 'lime' | 'cyan' | 'flame'; suffix?: string }) {
  const color = accent === 'lime' ? 'text-lime' : accent === 'cyan' ? 'text-cyan' : 'text-[#FF6B7A]';
  return (
    <div>
      <div className={`font-display font-extrabold text-2xl tabular-nums ${color}`}>
        {n}{suffix ?? ''}
      </div>
      <div className="text-2xs uppercase tracking-wider text-haze mt-0.5">{label}</div>
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 px-4 py-2 rounded-full text-sm font-semibold transition ${
        active
          ? 'bg-cyan/20 text-cyan border border-cyan/50 shadow-[0_0_18px_-8px_rgba(94,224,255,0.6)]'
          : 'bg-white/5 text-haze border border-line/60 hover:bg-white/10'
      }`}
    >
      {children}
    </button>
  );
}
